/* eslint-disable max-lines */
import { createStyles } from '@codeleap/styles'
import { ButtonComposition } from '@codeleap/web'
import { StyleRegistry } from '../styles'
import { textVariant } from './Text'

const createButtonVariant = createStyles<ButtonComposition>

export const ButtonStyles = {
  default: createButtonVariant(theme => ({
    wrapper: {
      width: 'auto',
      cursor: 'pointer',
      outline: 'none',
      borderRadius: theme.radius.regular,
      minHeight: theme.size['xx-large'],
      backgroundColor: theme.colors.buttonHighlightPrimaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonHighlightPrimaryBgDefault,
        width: theme.stroke.regular,
      }),
      ...theme.presets.row,
      ...theme.presets.relative,
      ...theme.presets.alignCenter,
      ...theme.presets.justifyCenter,
      ...theme.spacing.padding(1),
      ...theme.spacing.paddingHorizontal(2),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonHighlightPrimaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonHighlightPrimaryBgDisabled,
        width: theme.stroke.small,
      }),
      cursor: 'not-allowed',
    },
    icon: {
      color: theme.colors.buttonHighlightPrimaryIconDefault,
      width: theme.values.iconSize[2],
      height: theme.values.iconSize[2],
    },
    'icon:disabled': {
      color: theme.colors.buttonHighlightPrimaryIconDisabled,
    },
    leftIcon: {
      ...theme.spacing.marginRight('auto'),
    },
    rightIcon: {
      ...theme.spacing.marginLeft('auto'),
    },
    text: {
      textAlign: 'center',
      color: theme.baseColors.onPrimarySolid500Heading,
    },
    'text:disabled': {
      color: theme.baseColors.onPrimarySolid500Caption,
    },
    loaderWrapper: {
      height: theme.values.iconSize[4],
      width: theme.values.iconSize[4],
    },
  })),
  large: createButtonVariant(theme => ({
    wrapper: {
      height: theme.size.large,
    },
    text: {
      fontWeight: '500',
    },
    loaderWrapper: {
      width: theme.values.iconSize[5],
      height: theme.values.iconSize[5],
    },
    icon: {
      width: theme.values.iconSize[2],
      height: theme.values.iconSize[2],
    },
    leftIcon: {
      ...theme.spacing.marginRight(1),
    },
    rightIcon: {
      ...theme.spacing.marginLeft(1),
    },
  })),
  'xx-large': createButtonVariant(theme => ({
    wrapper: {
      height: theme.size['xx-large'],
    },
    text: {
      fontWeight: '500',
    },
    loaderWrapper: {
      width: theme.values.iconSize[5],
      height: theme.values.iconSize[5],
    },
    icon: {
      width: theme.values.iconSize[2],
      height: theme.values.iconSize[2],
    },
    leftIcon: {
      ...theme.spacing.marginRight(1),
    },
    rightIcon: {
      ...theme.spacing.marginLeft(1),
    },
  })),
  small: createButtonVariant(theme => ({
    wrapper: {
      height: theme.size.medium,
      ...theme.spacing.paddingHorizontal(1),
    },
    text: {
      ...textVariant('p3'),
      fontWeight: '500',
    },
    loaderWrapper: {
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
    },
    icon: {
      width: theme.values.iconSize[1],
      height: theme.values.iconSize[1],
    },
    leftIcon: {
      ...theme.spacing.marginLeft(1),
    },
    rightIcon: {
      ...theme.spacing.marginRight(1),
    },
  })),
  regularSecondary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonHighlightSecondaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonHighlightSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonHighlightSecondaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonHighlightSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonHighlightSecondaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonHighlightSecondaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonHighlightSecondaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonHighlightSecondaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonHighlightSecondaryIconDisabled,
    },
  })),
  regularTerciary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonHighlightTerciaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonHighlightTerciaryStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonHighlightTerciaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonHighlightTerciaryStrokeDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonHighlightTerciaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonHighlightTerciaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonHighlightTerciaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonHighlightTerciaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonHighlightTerciaryIconDisabled,
    },
  })),
  regularQuaternary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonHighlightQuaternaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonHighlightQuaternaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonHighlightQuaternaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonHighlightQuaternaryBgDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonHighlightQuaternaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonHighlightQuaternaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonHighlightQuaternaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonHighlightQuaternaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonHighlightQuaternaryIconDisabled,
    },
  })),
  neutralSecondary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNeutralSecondaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNeutralSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNeutralSecondaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNeutralSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNeutralSecondaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNeutralSecondaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNeutralSecondaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNeutralSecondaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNeutralSecondaryIconDisabled,
    },
  })),
  neutralTerciary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNeutralTerciaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNeutralTerciaryStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNeutralTerciaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNeutralTerciaryStrokeDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNeutralTerciaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNeutralTerciaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNeutralTerciaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNeutralTerciaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNeutralTerciaryIconDisabled,
    },
  })),
  neutralQuaternary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNeutralQuaternaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNeutralQuaternaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNeutralQuaternaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNeutralQuaternaryBgDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNeutralQuaternaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNeutralQuaternaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNeutralQuaternaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNeutralQuaternaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNeutralQuaternaryIconDisabled,
    },
  })),
  negativePrimary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNegativePrimaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNegativePrimaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNegativePrimaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNegativePrimaryBgDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNegativePrimaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNegativePrimaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNegativePrimaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNegativePrimaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNegativePrimaryIconDisabled,
    },
  })),
  negativeSecondary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNegativeSecondaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNegativeSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNegativeSecondaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNegativeSecondaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNegativeSecondaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNegativeSecondaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNegativeSecondaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNegativeSecondaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNegativeSecondaryIconDisabled,
    },
  })),
  negativeTerciary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNegativeTerciaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNegativeTerciaryStrokeDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNegativeTerciaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNegativeTerciaryStrokeDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNegativeTerciaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNegativeTerciaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNegativeTerciaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNegativeTerciaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNegativeTerciaryIconDisabled,
    },
  })),
  negativeQuaternary: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.colors.buttonNegativeQuaternaryBgDefault,
      ...theme.border({
        color: theme.colors.buttonNegativeQuaternaryBgDefault,
        width: theme.stroke.small,
      }),
    },
    'wrapper:disabled': {
      backgroundColor: theme.colors.buttonNegativeQuaternaryBgDisabled,
      ...theme.border({
        color: theme.colors.buttonNegativeQuaternaryBgDisabled,
        width: theme.stroke.small,
      }),
    },
    text: {
      color: theme.colors.buttonNegativeQuaternaryLabelDefault,
    },
    'text:disabled': {
      color: theme.colors.buttonNegativeQuaternaryLabelDisabled,
    },
    loader: {
      color: theme.colors.buttonNegativeQuaternaryIconDefault,
    },
    icon: {
      color: theme.colors.buttonNegativeQuaternaryIconDefault,
    },
    'icon:disabled': {
      color: theme.colors.buttonNegativeQuaternaryIconDisabled,
    },
  })),
  link: createButtonVariant(theme => ({
    wrapper: {
      textDecoration: 'none',
      color: theme.baseColors.neutralSolid1000,
    },
    text: {
      textDecoration: 'none',
      color: theme.baseColors.neutralSolid1000,
    },
  })),
  minimal: createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: 'transparent',
      ...theme.border({
        color: 'transparent',
        width: 0,
      }),
      ...theme.spacing.padding(0),
      ...theme.spacing.paddingHorizontal(0),
    },
    'wrapper:disabled': {
      backgroundColor: 'transparent',
      ...theme.border({
        color: 'transparent',
        width: 0,
      }),
      ...theme.spacing.padding(0),
      ...theme.spacing.paddingHorizontal(0),
    },
    'text:disabled': {
      color: theme.baseColors.primarySolid100,
    },
    text: {
      color: theme.baseColors.primarySolid500,
    },
  })),
  'link:outline': createButtonVariant(theme => ({
    wrapper: {
      backgroundColor: theme.baseColors.neutralSolid0,
      borderRadius: theme.radius.small,
      ...theme.border({
        color: theme.colors.buttonHighlightTerciaryStrokeDefault,
        width: theme.stroke.small,
      }),

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid0,
        ...theme.border({
          color: theme.colors.buttonHighlightTerciaryStrokeHover,
          width: theme.stroke.small,
        }),
      },
    },
    text: {
      color: theme.colors.buttonHighlightTerciaryLabelDefault,
    },
    icon: {
      color: theme.colors.buttonHighlightTerciaryIconDefault,
    },
  })),
  noPadding: createButtonVariant(theme => ({
    wrapper: {
      ...theme.spacing.padding(0),
    },
  })),
  pill: createButtonVariant(theme => ({
    wrapper: {
      borderRadius: theme.radius.large,
      ...theme.spacing.paddingHorizontal(1),
      ...theme.spacing.paddingVertical(0.5),
    },
  })),
  normal: createButtonVariant(theme => ({
    wrapper: {
      borderRadius: theme.radius.regular,
      ...theme.spacing.paddingHorizontal(1),
      ...theme.spacing.paddingVertical(0.5),
    },
  })),
  centerLeftIcon: createButtonVariant(theme => ({
    wrapper: {
      ...theme.presets.center,
      ...theme.presets.fullWidth,
    },
    leftIcon: {
      marginRight: 0,
    },
    rightIcon: {
      display: 'none',
    },
    text: {
      ...theme.spacing.marginLeft(1),
    },
  })),
  centerRightIcon: createButtonVariant(theme => ({
    wrapper: {
      ...theme.presets.center,
      ...theme.presets.fullWidth,
    },
    rightIcon: {
      marginLeft: 0,
    },
    leftIcon: {
      display: 'none',
    },
    text: {
      ...theme.spacing.marginRight(1),
    },
  })),
  row: createButtonVariant(theme => ({
    wrapper: {
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      ...theme.presets.justifyStart,
      ...theme.presets.fullWidth,
      ...theme.spacing.gap(1),
      ...theme.spacing.px(3),
      ...theme.spacing.py(2),
      border: 'none',
      ...theme.border({ color: theme.baseColors.neutralSolid300, width: 1, directions: ['bottom'] }),
      backgroundColor: 'transparent',
      borderRadius: 0,
    },
    leftIcon: {
      marginRight: 0,
    },
    rightIcon: {
      marginLeft: 'auto',
    },
    icon: {
      color: theme.baseColors.neutralSolid500,
      width: theme.values.iconSize[3],
      height: theme.values.iconSize[3],
    },
    text: {
      ...textVariant(['medium', 'p3']),
      color: theme.colors.textHeadingHighlighted,
    },
  })),
  'row:negative': createButtonVariant(theme => ({
    icon: {
      color: theme.baseColors.redSolid500,
    },
    text: {
      color: theme.baseColors.redSolid500,
    },
  })),

  textLeft: createButtonVariant(theme => ({
    leftIcon: {
      marginRight: theme.spacing.value(1),
    },
    text: {
      flex: 1,
      textAlign: 'left',
    },
  })),

  pillSmall: createButtonVariant(theme => ({
    wrapper: {
      borderRadius: theme.radius.large,
      ...theme.spacing.paddingHorizontal(0.5),
      ...theme.spacing.paddingVertical(0.25),
      minHeight: theme.size.large,
      ...theme.spacing.paddingHorizontal(1),
    },
  })),
}

StyleRegistry.registerVariants('Button', ButtonStyles)
